/**
 * Gift Selection Popup for Poker Reborn
 *
 * Opens when a player taps an opponent's avatar. Shows a grid of
 * available gifts organized by category tabs. Selecting a gift
 * triggers the throw animation.
 *
 * @see tasks/done/T42_TABLE_GIFTS_PLAN.md - Feature plan, UX flow
 * @see src/config/gifts.ts - Gift catalog
 */

import { Container, Graphics, Text } from 'pixi.js';
import { GIFT_CATALOG, getAvailableCategories } from '@/config/gifts';
import type { GiftCategory } from '@/types/gifts';
import { getDeviceValue } from '@/utils/responsive';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },
  popup: {
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    strokeWidth: 2,
    cornerRadius: 15,
  },
  title: {
    fill: 0xffffff,
    fontSize: 18,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  closeButton: {
    color: 0x888888,
    hoverColor: 0xffffff,
    fontSize: 24,
  },
  tab: {
    activeFill: 0x3a3a5e,
    inactiveFill: 0x252540,
    activeText: 0xffffff,
    inactiveText: 0x888888,
    fontSize: 13,
    fontFamily: 'Roboto, Arial, sans-serif',
    cornerRadius: 8,
    height: 30,
    gap: 6,
  },
  cell: {
    size: 70,
    gap: 8,
    fill: 0x252540,
    hoverFill: 0x3a3a5e,
    cornerRadius: 10,
    emojiFontSize: 32,
    priceFontSize: 11,
    priceColor: 0xaaaaaa,
    freeBadgeColor: 0x4ecdc4,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
} as const;

// =============================================================================
// CATEGORY LABELS
// =============================================================================

const CATEGORY_LABELS: Record<string, string> = {
  all: 'All',
  emoji: 'Emoji',
  food: 'Food',
  taunt: 'Taunts',
  celebration: 'Party',
  premium: 'Premium',
};

// =============================================================================
// GIFT POPUP
// =============================================================================

/**
 * Gift selection grid popup. Follows the established popup pattern:
 * - Extends Container
 * - Full-screen overlay (tap to dismiss)
 * - Centered panel with rounded rect background
 * - Arrow property event handlers for proper cleanup
 */
export class GiftPopup extends Container {
  /** Callback when a gift is selected */
  onGiftSelected: ((giftId: string) => void) | null = null;
  /** Callback when popup is dismissed */
  onDismiss: (() => void) | null = null;

  private overlay: Graphics;
  private panel: Container;
  private background: Graphics;

  private activeCategory: GiftCategory | 'all' = 'all';
  private tabContainer: Container;
  private gridContainer: Container;

  private readonly screenWidth: number;
  private readonly screenHeight: number;
  private readonly targetName: string;
  private readonly columnCount: number;

  constructor(
    screenWidth: number,
    screenHeight: number,
    targetPlayerName: string,
  ) {
    super();
    this.label = 'GiftPopup';
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.targetName = targetPlayerName;
    this.columnCount = getDeviceValue(screenWidth, { phone: 4, tablet: 5, desktop: 6 });

    // ── Overlay ──────────────────────────────────────────────────────────────
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointerdown', this.handleDismiss);
    this.addChild(this.overlay);

    // ── Panel ────────────────────────────────────────────────────────────────
    this.panel = new Container();
    this.panel.eventMode = 'static'; // Block clicks through to overlay
    this.panel.on('pointerdown', (e) => { e.stopPropagation(); });
    this.addChild(this.panel);

    // ── Background ───────────────────────────────────────────────────────────
    this.background = new Graphics();
    this.panel.addChild(this.background);

    // ── Tabs ─────────────────────────────────────────────────────────────────
    this.tabContainer = new Container();
    this.panel.addChild(this.tabContainer);

    // ── Grid ─────────────────────────────────────────────────────────────────
    this.gridContainer = new Container();
    this.panel.addChild(this.gridContainer);

    // Build UI
    this.buildUI();
  }

  // ---------------------------------------------------------------------------
  // UI Construction
  // ---------------------------------------------------------------------------

  private buildUI(): void {
    const { cell, tab } = STYLES;
    const padding = 20;
    const headerHeight = 45;
    const tabsHeight = tab.height + 10;

    // Calculate popup dimensions from grid
    const gridWidth = this.columnCount * (cell.size + cell.gap) - cell.gap;
    const popupWidth = gridWidth + padding * 2;

    // Calculate grid rows needed for "all" category (max items)
    const maxRows = Math.ceil(GIFT_CATALOG.length / this.columnCount);
    const gridHeight = maxRows * (cell.size + cell.gap) - cell.gap;

    const popupHeight = headerHeight + tabsHeight + gridHeight + padding * 2 + 10;

    // Center panel
    this.panel.x = (this.screenWidth - popupWidth) / 2;
    this.panel.y = (this.screenHeight - popupHeight) / 2;

    // Draw background
    this.background.clear();
    this.background.roundRect(0, 0, popupWidth, popupHeight, STYLES.popup.cornerRadius);
    this.background.fill(STYLES.popup.fill);
    this.background.stroke({ width: STYLES.popup.strokeWidth, color: STYLES.popup.stroke });

    // ── Header ───────────────────────────────────────────────────────────────
    const title = new Text({
      text: `SEND GIFT TO ${this.targetName.toUpperCase()}`,
      style: STYLES.title,
    });
    title.x = padding;
    title.y = padding;
    this.panel.addChild(title);

    // Close button
    const closeButton = new Text({
      text: '✕',
      style: {
        fontSize: STYLES.closeButton.fontSize,
        fill: STYLES.closeButton.color,
        fontFamily: 'Arial, sans-serif',
      },
    });
    closeButton.x = popupWidth - padding - closeButton.width;
    closeButton.y = padding - 2;
    closeButton.eventMode = 'static';
    closeButton.cursor = 'pointer';
    closeButton.on('pointerdown', this.handleDismiss);
    closeButton.on('pointerover', () => { closeButton.style.fill = STYLES.closeButton.hoverColor; });
    closeButton.on('pointerout', () => { closeButton.style.fill = STYLES.closeButton.color; });
    this.panel.addChild(closeButton);

    // ── Category Tabs ────────────────────────────────────────────────────────
    this.tabContainer.x = padding;
    this.tabContainer.y = headerHeight + padding;
    this.buildTabs();

    // ── Gift Grid ────────────────────────────────────────────────────────────
    this.gridContainer.x = padding;
    this.gridContainer.y = headerHeight + padding + tabsHeight;
    this.buildGrid();
  }

  private buildTabs(): void {
    this.tabContainer.removeChildren();

    const categories: Array<GiftCategory | 'all'> = ['all', ...getAvailableCategories()];
    let tabX = 0;

    for (const category of categories) {
      const label = CATEGORY_LABELS[category] ?? category;
      const isActive = category === this.activeCategory;

      const tabText = new Text({
        text: label,
        style: {
          fontSize: STYLES.tab.fontSize,
          fill: isActive ? STYLES.tab.activeText : STYLES.tab.inactiveText,
          fontFamily: STYLES.tab.fontFamily,
        },
      });

      const tabWidth = tabText.width + 16;
      const tabBg = new Graphics();
      tabBg.roundRect(0, 0, tabWidth, STYLES.tab.height, STYLES.tab.cornerRadius);
      tabBg.fill(isActive ? STYLES.tab.activeFill : STYLES.tab.inactiveFill);

      const tabItem = new Container();
      tabItem.addChild(tabBg);
      tabText.x = 8;
      tabText.y = (STYLES.tab.height - tabText.height) / 2;
      tabItem.addChild(tabText);

      tabItem.x = tabX;
      tabItem.eventMode = 'static';
      tabItem.cursor = 'pointer';

      // Capture category for closure
      const capturedCategory = category;
      tabItem.on('pointerdown', () => {
        this.activeCategory = capturedCategory;
        this.buildTabs();
        this.buildGrid();
      });

      this.tabContainer.addChild(tabItem);
      tabX += tabWidth + STYLES.tab.gap;
    }
  }

  private buildGrid(): void {
    this.gridContainer.removeChildren();

    const gifts = this.activeCategory === 'all'
      ? GIFT_CATALOG
      : GIFT_CATALOG.filter((gift) => gift.category === this.activeCategory);

    const { size, gap, fill, cornerRadius, emojiFontSize, priceFontSize, priceColor, freeBadgeColor, fontFamily } = STYLES.cell;

    for (let i = 0; i < gifts.length; i++) {
      const gift = gifts[i];
      if (!gift) continue;

      const col = i % this.columnCount;
      const row = Math.floor(i / this.columnCount);

      const cellContainer = new Container();
      cellContainer.x = col * (size + gap);
      cellContainer.y = row * (size + gap);

      // Cell background
      const cellBg = new Graphics();
      cellBg.roundRect(0, 0, size, size, cornerRadius);
      cellBg.fill(fill);
      cellContainer.addChild(cellBg);

      // Emoji
      const emojiText = new Text({
        text: gift.emoji,
        style: { fontSize: emojiFontSize },
      });
      emojiText.anchor.set(0.5);
      emojiText.x = size / 2;
      emojiText.y = size * 0.38;
      cellContainer.addChild(emojiText);

      // Price label — show gold icon (🪙) for gold-currency gifts
      const pricePrefix = gift.currency === 'gold' ? '🪙 ' : '';
      const priceLabel = gift.price === 0 ? 'FREE' : `${pricePrefix}${this.formatPrice(gift.price)}`;
      const priceText = new Text({
        text: priceLabel,
        style: {
          fontSize: priceFontSize,
          fill: gift.price === 0 ? freeBadgeColor : priceColor,
          fontFamily,
          fontWeight: gift.price === 0 ? 'bold' : 'normal',
        },
      });
      priceText.anchor.set(0.5);
      priceText.x = size / 2;
      priceText.y = size * 0.75;
      cellContainer.addChild(priceText);

      // Interactivity
      cellContainer.eventMode = 'static';
      cellContainer.cursor = 'pointer';

      // Hover effect
      cellContainer.on('pointerover', () => {
        cellBg.clear();
        cellBg.roundRect(0, 0, size, size, cornerRadius);
        cellBg.fill(STYLES.cell.hoverFill);
      });
      cellContainer.on('pointerout', () => {
        cellBg.clear();
        cellBg.roundRect(0, 0, size, size, cornerRadius);
        cellBg.fill(fill);
      });

      // Select gift
      const capturedGiftId = gift.id;
      cellContainer.on('pointerdown', (e) => {
        e.stopPropagation();
        this.onGiftSelected?.(capturedGiftId);
        this.handleDismiss();
      });

      this.gridContainer.addChild(cellContainer);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private formatPrice(amount: number): string {
    if (amount >= 1000) {
      return `${String(Math.floor(amount / 1000))}K`;
    }
    return String(amount);
  }

  // ---------------------------------------------------------------------------
  // Event Handlers (arrow properties for proper off() cleanup)
  // ---------------------------------------------------------------------------

  private handleDismiss = (): void => {
    this.onDismiss?.();
    this.destroy();
  };

  // ---------------------------------------------------------------------------
  // Resize
  // ---------------------------------------------------------------------------

  resize(width: number, height: number): void {
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Recenter panel
    const panelBounds = this.background.getBounds();
    this.panel.x = (width - panelBounds.width) / 2;
    this.panel.y = (height - panelBounds.height) / 2;
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  override destroy(): void {
    this.onGiftSelected = null;
    this.onDismiss = null;
    super.destroy({ children: true });
  }
}
